/* istanbul ignore file */

import type { Client } from 'urql';
import type { convertGooglePlacesAddress } from '@sg/garnish';
import { formatGoogleAddress } from '@sg/garnish';

import type { CustomerAddressesQuery } from '../../graphql/CustomerDeliveryDetails.generated';
import type { DeliveryLocationByAddressQueryVariables } from '../../graphql/DeliveryLocationByAddress.generated';
import type { LastPlacedOrderDetailsQuery } from '../../graphql/LastPlacedOrders.generated';
import type {
  DeliveryLocationWithoutNearbyStore,
  Location,
} from '../../LocationSearch.types';
import {
  activeCartDeliveryDetailsQuery,
  customerAddressesQuery,
  deliveryLocationByAddressQuery,
  lastPlacedOrdersDetailsQuery,
} from '../../queries';
import { getDeliveryLocationByPlaceId } from './delivery-location';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetches delivery location details based on the provided address.
 */
export async function getDeliveryLocationDetails(
  client: Client,
  googlePlaceAddress: GooglePlaceAddress,
): Promise<Location | DeliveryLocationWithoutNearbyStore> {
  const deliveryLocation = await getDeliveryLocation(
    client,
    convertAddressToDeliveryLocationInput(googlePlaceAddress),
  );

  if (!deliveryLocation)
    throw new Error('Failed to get delivery location details');

  // ─── Delivery Location Without Nearby Store ──────────────────────────

  if (deliveryLocation.__typename === 'NoValidRestaurants') {
    return getWithoutNearbyStoreDeliveryLocationDetails(googlePlaceAddress);
  }

  // ─── Delivery Location With Nearby Store ──────────────────────────

  return getWithNearbyStoreDeliveryLocationDetails({
    client,
    googlePlaceAddress,
    deliveryLocation,
  });
}

/**
 * Fetches a nearby location for the specified delivery address.
 */
async function getDeliveryLocation(
  client: Client,
  input: DeliveryLocationByAddressQueryVariables['input'],
) {
  const { data } = await deliveryLocationByAddressQuery(client, { input });
  const { locationByAddress } = data ?? {};

  if (
    locationByAddress?.__typename !== 'Location' &&
    locationByAddress?.__typename !== 'NoValidRestaurants'
  )
    throw new Error('Failed to get delivery location');

  return locationByAddress;
}

/**
 * Fetches the active cart delivery location (if available).
 */
export async function getActiveCartDeliveryLocation(
  props: GetActiveCartDeliveryLocationProps,
) {
  const { client, googlePlacesApiKey } = props;

  const { data } = await activeCartDeliveryDetailsQuery(client);
  const { cart } = data ?? {};

  const activeCartDeliveryLocationGooglePlaceId =
    cart?.deliveryOrderDetail?.address?.googlePlaceId;

  if (!activeCartDeliveryLocationGooglePlaceId) return;

  return getDeliveryLocationByPlaceId({
    client,
    googlePlaceId: activeCartDeliveryLocationGooglePlaceId,
    googlePlacesApiKey,
  });
}

/**
 * Fetches the last interacted store delivery location (if available).
 */
export async function getLastInteractedStoreDeliveryLocation(
  props: GetLastInteractedStoreDeliveryLocation,
) {
  const { client, googlePlacesApiKey, lastInteractedStoreAddressId } = props;

  const lastInteractedStore = await getCustomerDeliveryAddressByAddressId(
    client,
    lastInteractedStoreAddressId,
  );
  const lastInteractedStoreGooglePlaceId = lastInteractedStore?.googlePlaceId;

  if (!lastInteractedStoreGooglePlaceId) return;

  return getDeliveryLocationByPlaceId({
    client,
    googlePlaceId: lastInteractedStoreGooglePlaceId,
    googlePlacesApiKey,
  });
}

/**
 * Attempts to get the last placed order delivery address ID.
 */
export async function getLastPlacedOrderDeliveryLocation(
  props: GetLastPlacedOrderDeliveryLocation,
) {
  const { client, googlePlacesApiKey } = props;

  const { data: customerData } = await customerAddressesQuery(client);
  const isSignedInCustomer = customerData?.currentCustomer?.id !== undefined;

  // NOTE: We only query the last placed order only for signed-in customers.
  if (!isSignedInCustomer) return;

  const customerDeliveryAddress = await getCustomerDeliveryAddresses(client);
  const { data } = await lastPlacedOrdersDetailsQuery(client);
  const { orders: ordersData } = data ?? {};

  const hasLastPlacedOrder = ordersData?.__typename === 'OrdersResponseSuccess';
  const lastPlacedOrder = hasLastPlacedOrder
    ? findLastPlacedValidDeliveryOrder(
        ordersData.orders,
        customerDeliveryAddress,
      )
    : undefined;

  const lastPlacedOrderGooglePlaceId =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (lastPlacedOrder?.deliveryOrderDetail as any)?.address?.googlePlaceId;

  if (!lastPlacedOrderGooglePlaceId) return;

  return getDeliveryLocationByPlaceId({
    client,
    googlePlaceId: lastPlacedOrderGooglePlaceId,
    googlePlacesApiKey,
  });
}

/**
 * Attempts to get the customer first previously saved delivery location.
 */
export async function getCustomerFirstDeliveryLocation(
  props: GetCustomerFirstDeliveryLocation,
) {
  const { client, googlePlacesApiKey } = props;

  const customerDeliveryAddresses = await getCustomerDeliveryAddresses(client);

  const customerFirstDeliveryAddressGooglePlaceId =
    customerDeliveryAddresses?.[0]?.googlePlaceId;

  if (!customerFirstDeliveryAddressGooglePlaceId) return;

  return getDeliveryLocationByPlaceId({
    client,
    googlePlaceId: customerFirstDeliveryAddressGooglePlaceId,
    googlePlacesApiKey,
  });
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Attempts to get customer's delivery addresses.
 */
export async function getCustomerDeliveryAddresses(client: Client) {
  const { data } = await customerAddressesQuery(client);

  return data?.currentCustomer?.addresses ?? [];
}

/**
 * Attempts to get the customer's delivery address based on the provided address ID.
 */
export async function getCustomerDeliveryAddressByAddressId(
  client: Client,
  addressId: string,
) {
  const customerAddresses = await getCustomerDeliveryAddresses(client);

  return customerAddresses.find(
    (customerAddress) => customerAddress.id === addressId,
  );
}

/**
 * Attempts to get the customer's delivery address based on the provided Google place ID.
 */
export async function getCustomerDeliveryAddressByGooglePlaceId(
  client: Client,
  googlePlaceId: string,
) {
  const customerAddresses = await getCustomerDeliveryAddresses(client);

  return customerAddresses.find(
    (customerAddress) => customerAddress.googlePlaceId === googlePlaceId,
  );
}

/**
 * Attempts to get an existing customer delivery address name, based on the provided Google place ID.
 */
export async function getDeliveryAddressName(
  client: Client,
  googlePlaceId: string,
) {
  const existingAddress = await getCustomerDeliveryAddressByGooglePlaceId(
    client,
    googlePlaceId,
  );

  return existingAddress?.name;
}

/**
 * Attempts to get an existing customer delivery address ID, based on the provided Google place ID.
 */
export async function getExistingCustomerDeliveryAddressId(
  client: Client,
  googlePlaceId: string,
) {
  const existingAddress = await getCustomerDeliveryAddressByGooglePlaceId(
    client,
    googlePlaceId,
  );

  return existingAddress?.id;
}

/**
 * Attempts to find a previously placed delivery order.
 */
function findLastPlacedValidDeliveryOrder(
  lastPlacedOrders: LastPlacedOrders,
  customDeliveryAddresses: CustomerDeliveryAddresses,
) {
  const customDeliveryAddressesSet = new Set(
    customDeliveryAddresses.map(({ id }) => id),
  );

  return lastPlacedOrders.find(({ deliveryOrderDetail }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const deliveryAddressId = (deliveryOrderDetail as any)?.address?.id;

    return (
      deliveryAddressId !== undefined &&
      typeof deliveryAddressId === 'string' &&
      customDeliveryAddressesSet.has(deliveryAddressId)
    );
  });
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns delivery location (WITH nearby store) details based on the provided
 * Google Place address.
 */
async function getWithNearbyStoreDeliveryLocationDetails(
  props: GetWithNearbyStoreDeliveryLocationDetails,
): Promise<Location> {
  const { client, googlePlaceAddress, deliveryLocation } = props;

  const {
    googlePlaceId,
    longitude,
    latitude,
    city,
    state,
    country,
    zipCode,
    street,
  } = googlePlaceAddress;

  const estimatedDeliveryTime = getDeliveryLocationETA(deliveryLocation);
  const deliveryFormattedAddress = formatGoogleAddress(googlePlaceAddress);
  const deliveryAddressId = await getExistingCustomerDeliveryAddressId(
    client,
    googlePlaceId,
  );
  const deliveryAddressName = await getDeliveryAddressName(
    client,
    googlePlaceId,
  );
  const isDeliveryLocationClosed = deliveryLocation?.status === 'CLOSED';

  const {
    id: restaurantId,
    name: restaurantName,
    slug: restaurantSlug,
    flexMessage,
    phone,
    deliveryFee,
  } = deliveryLocation?.restaurant ?? {};
  const { name: deliveryVendorName, restaurantId: deliveryVendorRestaurantId } =
    deliveryLocation.vendor;

  // ─────────────────────────────────────────────────────────────────────

  return {
    id: googlePlaceId,
    addressId: deliveryAddressId ?? '',
    name: deliveryAddressName ?? '',
    address: deliveryFormattedAddress ?? '',
    estimatedDeliveryTime,
    flexMessage,
    phone,
    isClosed: isDeliveryLocationClosed,
    lat: latitude,
    lng: longitude,
    street,
    zipCode,
    city,
    state,
    country,
    restaurantSlug,
    restaurantName,
    restaurantId,
    restaurantDeliveryFee: deliveryFee,
    deliveryVendorName,
    deliveryVendorRestaurantId,
  };
}

/**
 * Returns delivery location (WITHOUT nearby store) details based on the provided
 * Google Place address.
 */
function getWithoutNearbyStoreDeliveryLocationDetails(
  googlePlaceAddress: GooglePlaceAddress,
): DeliveryLocationWithoutNearbyStore {
  const deliveryFormattedAddress = formatGoogleAddress(googlePlaceAddress);

  return {
    id: googlePlaceAddress.googlePlaceId,
    lat: googlePlaceAddress.latitude,
    lng: googlePlaceAddress.longitude,
    street: googlePlaceAddress.street,
    zipCode: googlePlaceAddress.zipCode,
    city: googlePlaceAddress.city,
    state: googlePlaceAddress.state,
    country: googlePlaceAddress.country,
    address: deliveryFormattedAddress,
    name: '',
    restaurantSlug: undefined,
    restaurantId: '',
    restaurantName: '',
    addressId: undefined,
    deliveryVendorName: undefined,
    deliveryVendorRestaurantId: undefined,
    restaurantDeliveryFee: undefined,
  };
}

/**
 * Returns a formatted location address that does not include the country term.
 */
function withoutCountryTerm(
  address: string,
  terms: ReadonlyArray<Readonly<{ value: string }>>,
) {
  const countryTerm = terms.at(-1)?.value;
  const countrySubstring = `, ${countryTerm}`;

  if (address.endsWith(countrySubstring)) {
    return address.replace(countrySubstring, '');
  }

  return address;
}

/**
 * Returns a formatter function for Google Autocomplete Predictions
 */
export function formatDeliveryAddressPrediction(
  customerDeliveryAddresses: CustomerDeliveryAddresses,
) {
  return function (predictionAddress: AutocompletePrediction) {
    const {
      place_id: id,
      description,
      terms,
      matched_substrings: matchedSubstrings,
    } = predictionAddress;

    const customerExistingDeliveryAddressName =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      getCustomerExistingDeliveryAddressName(customerDeliveryAddresses, id);
    const name = customerExistingDeliveryAddressName ?? '';
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const address = withoutCountryTerm(description, terms);

    return { id, name, address, matchedSubstrings };
  };
}

/**
 * Returns estimated delivery time for the provided delivery location.
 */
function getDeliveryLocationETA(
  deliveryLocation: MaybeDeliveryLocationWithETA,
) {
  const { start = 0, end = 0 } = deliveryLocation?.estimateRange ?? {};

  return start === end ? `${start}` : `${start}-${end}`;
}

/**
 * Attempts to get the name of customer's matching delivery address based on the provided Google Place ID.
 */
function getCustomerExistingDeliveryAddressName(
  customerDeliveryAddresses: CustomerDeliveryAddresses,
  googlePlaceId: string,
) {
  return customerDeliveryAddresses?.find(
    (customerDeliveryAddress) =>
      customerDeliveryAddress.googlePlaceId === googlePlaceId,
  )?.name;
}

/**
 * Convert the specified delivery address into an input
 * object needed to retrieve the delivery location.
 */
function convertAddressToDeliveryLocationInput(
  deliveryAddress: GooglePlaceAddress,
): DeliveryLocationByAddressQueryVariables['input'] {
  const {
    googlePlaceId,
    longitude: lon,
    latitude: lat,
    street: address1,
    city,
    state,
    zipCode,
  } = deliveryAddress;
  const maybeZipCode = Number.isNaN(zipCode)
    ? {}
    : { zipcode: Number(zipCode) };

  return {
    address1,
    city,
    lat,
    lon,
    state,
    googlePlaceId,
    ...maybeZipCode,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type GetActiveCartDeliveryLocationProps = Readonly<{
  client: Client;
  googlePlacesApiKey: string;
}>;

type GetLastInteractedStoreDeliveryLocation = Readonly<{
  client: Client;
  googlePlacesApiKey: string;
  lastInteractedStoreAddressId: string;
}>;

type GetLastPlacedOrderDeliveryLocation = Readonly<{
  client: Client;
  googlePlacesApiKey: string;
}>;

type GetCustomerFirstDeliveryLocation = Readonly<{
  client: Client;
  googlePlacesApiKey: string;
}>;

type GetWithNearbyStoreDeliveryLocationDetails = Readonly<{
  client: Client;
  googlePlaceAddress: GooglePlaceAddress;
  deliveryLocation: ValidDeliveryLocation;
}>;

type ValidDeliveryLocation = Extract<
  Awaited<ReturnType<typeof getDeliveryLocation>>,
  Readonly<{ __typename: 'Location' }>
>;

type AutocompletePrediction = google.maps.places.AutocompletePrediction;

type GooglePlaceAddress = Awaited<
  ReturnType<typeof convertGooglePlacesAddress>
>;

type CustomerDeliveryAddresses = NonNullable<
  CustomerAddressesQuery['currentCustomer']
>['addresses'];

type MaybeDeliveryLocationWithETA = Readonly<{
  estimateRange?: {
    start: number;
    end: number;
  };
}>;

type LastPlacedOrders = Extract<
  LastPlacedOrderDetailsQuery['orders'],
  Readonly<{ __typename: 'OrdersResponseSuccess' }>
>['orders'];
